import React from 'react';
import { Check, RefreshCcw, Save } from 'lucide-react';

import PriceDistributionView from '../PriceDistributionView';
import { formatConfidencePercent } from '../../utils/formatters';

const conditionBadgeClass = (condition) => {
  if (condition === 'New') return 'bg-positive/10 text-positive';
  if (condition === 'Damaged' || condition === 'For Parts') return 'bg-danger/10 text-danger';
  if (condition === 'Unknown') return 'bg-surface-2 text-muted';
  return 'bg-warn/10 text-warn';
};

const sourceBadge = (priceSource) => {
  if (priceSource === 'ebay_rag') return { label: 'eBay RAG', className: 'bg-teal/10 text-teal' };
  if (priceSource === 'image') return { label: 'Image', className: 'bg-warn/10 text-warn' };
  return { label: 'Model Knowledge', className: 'bg-surface-2 text-muted' };
};

const HistoryGroupMobileList = ({
  group,
  originalItemsById,
  updatingId,
  updatingNameId,
  onOpenItemImage,
  onManualNameChange,
  onUpdateItemName,
  onPriceChange,
  onUpdatePrice,
}) => {
  return (
    <div className="sm:hidden divide-y divide-hairline">
      {group.items.map((item) => {
        const originalItem = originalItemsById[item.id];
        const isDirty = originalItem && parseFloat(originalItem.actual_price) !== parseFloat(item.actual_price);
        const isUpdating = updatingId === item.id;
        const isNameDirty = originalItem && (originalItem.manual_name || '') !== (item.manual_name || '');
        const isNameUpdating = updatingNameId === item.id;
        const condition = item?.condition || 'Unknown';
        const source = sourceBadge(item?.price_source || 'model_knowledge');

        return (
          <div key={item.id} className="p-3 space-y-2">
            <button onClick={() => onOpenItemImage(item)} className="text-sm font-medium text-ink hover:text-accent hover:underline transition-colors text-left">
              {item.item_name}
            </button>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted shrink-0">Manual:</span>
              <div className="relative flex items-center flex-1">
                <input
                  type="text"
                  placeholder="Custom name..."
                  value={item.manual_name || ''}
                  onChange={(e) => onManualNameChange(item.id, e.target.value)}
                  className={`w-full pr-8 py-1 px-2 border rounded text-sm text-ink focus:outline-none focus:ring-1 transition-all ${
                    isNameDirty ? 'border-accent focus:ring-accent bg-accent/5' : 'border-hairline focus:ring-positive bg-ink/[0.04]'
                  }`}
                />
                <button
                  onClick={() => onUpdateItemName(item.id, item.manual_name || '')}
                  disabled={!isNameDirty || isNameUpdating}
                  className={`absolute right-1 p-1 rounded transition-all ${
                    isNameUpdating ? 'text-muted' : (isNameDirty ? 'text-accent hover:bg-accent/10' : 'text-positive cursor-default')
                  }`}
                >
                  {isNameUpdating ? <RefreshCcw size={14} className="animate-spin" /> : (isNameDirty ? <Save size={14} /> : <Check size={14} />)}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm text-positive font-bold">${item.estimated_value?.toLocaleString()}</span>
              <span className="text-xs text-ai font-semibold">Conf: {formatConfidencePercent(item?.confidence_score)}</span>
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted">Actual:</span>
                <div className="relative inline-flex items-center">
                  <span className="absolute left-2 text-muted text-sm">$</span>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={item.actual_price}
                    onChange={(e) => onPriceChange(item.id, e.target.value)}
                    className={`w-24 pl-5 pr-8 py-1 border rounded text-sm text-ink focus:outline-none focus:ring-1 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                      isDirty ? 'border-accent focus:ring-accent bg-accent/5' : 'border-hairline focus:ring-positive bg-ink/[0.04]'
                    }`}
                    step="0.01"
                  />
                  <button
                    onClick={() => onUpdatePrice(item.id, item.actual_price)}
                    disabled={!isDirty || isUpdating}
                    className={`absolute right-1 p-1 rounded transition-all ${
                      isUpdating ? 'text-muted' : (isDirty ? 'text-accent hover:bg-accent/10' : 'text-positive cursor-default')
                    }`}
                  >
                    {isUpdating ? <RefreshCcw size={14} className="animate-spin" /> : (isDirty ? <Save size={14} /> : <Check size={14} />)}
                  </button>
                </div>
              </div>
            </div>
            <PriceDistributionView
              rangeValue={item?.estimated_value_range}
              distributionValue={item?.price_estimate_distribution}
              maxBins={3}
              compact
            />
            <div className="flex items-center gap-2">
              <span className="text-[9px] text-muted">Condition:</span>
              <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${conditionBadgeClass(condition)}`}>
                {condition}
              </span>
              <span className="text-[9px] text-muted ml-1">Price Src:</span>
              <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${source.className}`}>
                {source.label}
              </span>
            </div>
          </div>
        );
      })}

      {group.items.length >= 1 && (
        <div className="p-3 bg-surface-2 border-t border-hairline flex justify-between items-center">
          <span className="text-xs font-bold text-muted uppercase tracking-wider">Est. Sum</span>
          <span className="text-sm text-positive font-bold">
            ${group.items.reduce((sum, i) => sum + (parseFloat(i.estimated_value) || 0), 0).toLocaleString()}
          </span>
        </div>
      )}
    </div>
  );
};

export default HistoryGroupMobileList;
